/* eslint-disable */
import { rootApi } from '../rootApi';

/**
 * Birleşik Bayi Kaydı (Draft) + Onaya Gönder akışı endpoint'leri.
 * Tüm istekler languageId taşır (POST body -> languageId, GET query -> LanguageId/languageId).
 * Cevaplar ortak ResponseInfo<T> zarfındadır: { isSuccess, statusCode, message, data }.
 */
export const dealerRegistrationApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        // Birleşik kayıt -> taslak oluşturur (onay AÇMAZ). Dönüş id'leriyle sekmeler aktifleşir.
        saveDealerRegistration: builder.mutation({
            query: (body) => ({
                url: 'v1/DealerRegistration/Save',
                method: 'POST',
                body
            }),
            transformResponse: (response) => response,
            invalidatesTags: ['DealerRegistration']
        }),

        // Edit modunda üst bölüm (bayi + addresses[]) düzenlemesi -> staging.
        // Gövde sapDealerId + addresses[] taşır. Pending iken 409 döner.
        updateDealerRegistration: builder.mutation({
            query: (body) => ({
                url: 'v1/DealerRegistration/Update',
                method: 'POST',
                body
            }),
            transformResponse: (response) => response,
            invalidatesTags: (result, error, arg) => ['DealerRegistration', { type: 'DealerRegistration', id: arg?.sapDealerId ?? arg?.dealerAddressBrandId }]
        }),

        // Onaya Gönder -> Draft/Returned talebi tek birleşik onaya paketler. Sonra kilit.
        // Opsiyonel `payload` (üst bölüm Update gövdesiyle aynı alan seti) gönderilirse
        // backend önce staging'e kaydeder sonra onaya gönderir (tek çağrıda kaydet+gönder).
        sendDealerRegistrationToApproval: builder.mutation({
            query: ({ sapDealerId, dealerAddressBrandId, languageId, payload }) => {
                const body = { languageId };
                if (sapDealerId != null && sapDealerId !== '') body.sapDealerId = sapDealerId;
                else if (dealerAddressBrandId != null && dealerAddressBrandId !== '') body.dealerAddressBrandId = dealerAddressBrandId;
                if (payload) body.payload = payload;
                return { url: 'v1/DealerRegistration/SendToApproval', method: 'POST', body };
            },
            transformResponse: (response) => response,
            invalidatesTags: ['DealerRegistration', 'DealerApproval']
        }),

        // Birleşik liste (her satır = bayi/adres/marka + onay durumu).
        getDealerRegistrationList: builder.query({
            query: (params = {}) => {
                const body = {
                    PageNumber: params.PageNumber ?? 1,
                    PageSize: params.PageSize ?? 10,
                    sapDealerId: params.sapDealerId > 0 ? params.sapDealerId : undefined,
                    dealerId: params.dealerId > 0 ? params.dealerId : undefined,
                    brandId: params.brandId > 0 ? params.brandId : undefined,
                    overallStatus: params.overallStatus ?? undefined,
                    overallStatuses: params.overallStatuses?.length ? params.overallStatuses : undefined,
                    country: params.country || undefined,
                    includeRejected: params.includeRejected ?? false,
                    contractStartDate: params.contractStartDate || undefined,
                    contractEndDate: params.contractEndDate || undefined,
                    languageId: params.languageId ?? 2
                };
                Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
                return { url: 'v1/DealerRegistration/GetList', method: 'POST', body };
            },
            transformResponse: (response) => {
                if (response?.data && Array.isArray(response.data.items)) {
                    return { items: response.data.items, totalCount: response.data.totalCount ?? response.data.items.length };
                }
                return { items: [], totalCount: 0 };
            },
            providesTags: ['DealerRegistration']
        }),

        // Liste durumu filtresi dropdown kaynağı: [{ code, key }] — etiketler FE i18n'den üretilir.
        getDealerRegistrationStatusFilterList: builder.query({
            query: () => ({
                url: 'v1/DealerRegistration/GetStatusFilterList',
                method: 'POST',
                body: {}
            }),
            transformResponse: (response) => (Array.isArray(response?.data) ? response.data : []),
            providesTags: ['DealerRegistration']
        }),

        // Liste ülke filtresi dropdown kaynağı: yalnızca listede kaydı olan ülkeler.
        // value/label = country (dealer_address_t.country, case-sensitive eşleşme).
        getDealerRegistrationCountryFilterList: builder.query({
            query: () => ({
                url: 'v1/DealerRegistration/GetCountryFilterList',
                method: 'POST',
                body: {}
            }),
            transformResponse: (response) => (Array.isArray(response?.data) ? response.data : []),
            providesTags: ['DealerRegistration']
        }),

        // Liste filtresi dropdown'ı için veri kaynağı: yalnızca listede fiilen kaydı
        // olan bayiler (tekilleştirilmiş, ada göre sıralı). value=sapDealerId, label=name.
        getDealerFilterList: builder.query({
            query: ({ searchText = null, rowCount = 50 } = {}) => ({
                url: 'v1/DealerRegistration/GetDealerFilterList',
                method: 'POST',
                body: { searchText: searchText || null, rowCount }
            }),
            transformResponse: (response) => (Array.isArray(response?.data) ? response.data : []),
            providesTags: ['DealerRegistration']
        }),

        // Birleşik detay (bayi+adres+marka + onay durumu + tabsEnabled + rejectionReason).
        getDealerRegistrationById: builder.query({
            query: ({ id, sapDealerId, languageId = 2 }) => {
                const params = { languageId };
                if (sapDealerId != null && sapDealerId !== '') params.sapDealerId = sapDealerId;
                if (id != null && id !== '') params.id = id;
                return {
                    url: 'v1/DealerRegistration/GetById',
                    method: 'GET',
                    params
                };
            },
            transformResponse: (response) => (response?.data ? response.data : {}),
            providesTags: (result, error, arg) => [{ type: 'DealerRegistration', id: arg?.sapDealerId ?? arg?.id }]
        })
    })
});

export const {
    useSaveDealerRegistrationMutation,
    useUpdateDealerRegistrationMutation,
    useSendDealerRegistrationToApprovalMutation,
    useGetDealerRegistrationListQuery,
    useLazyGetDealerRegistrationListQuery,
    useGetDealerFilterListQuery,
    useLazyGetDealerFilterListQuery,
    useGetDealerRegistrationStatusFilterListQuery,
    useGetDealerRegistrationCountryFilterListQuery,
    useGetDealerRegistrationByIdQuery,
    useLazyGetDealerRegistrationByIdQuery
} = dealerRegistrationApi;
